import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Complaint, ComplaintFilters, ComplaintsDocumentType, ComplaintUserType, DocumentStatus } from '@icc/b2b/shared';
import { ComplaintPartyListItem } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintService } from '../complaint.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '@icc/shared';

@Component({
    selector: 'icc-complaint-filters',
    templateUrl: './complaint-filters.component.html',
    styleUrls: ['./complaint-filters.component.scss'],
})
export class ComplaintFiltersComponent implements OnInit, OnChanges {
    @Output() showMap = new EventEmitter<boolean>();
    @Input() isShowMap = false;

    client = '';
    nrOrder = '';
    distributorOrRetailer = '';
    status = 0;
    creationDateFrom: Date = null;
    creationDateTo: Date = null;
    assignedUserId = 0;
    userTypeCompanyId = 0;
    assignedTo: ComplaintUserType | null = null;
    companyId = 0;
    complaintType: ComplaintsDocumentType | 0 = 0;
    availableComplaintTypes: Array<{ value: number; label: string }> = [];
    filteredContractorsList: ComplaintPartyListItem[] = [];
    filteredContractors: ComplaintPartyListItem[] = [];
    accountManagerPersonId = 0;
    protected _filters: ComplaintFilters;

    @Input() isOrderList = false;
    @Input() isInquiryList = false;
    @Input() workersList: Record<string, string> | null = null;
    @Input() accountManagersList: Record<string, string> | null = null;
    @Input() contractorsList: ComplaintPartyListItem[] = [];
    @Input()
    get filters(): ComplaintFilters {
        return this._filters;
    }
    set filters(filters: ComplaintFilters) {
        this.setFilters(filters);
    }
    @Input() documentStatuses: DocumentStatus[];

    @Input() side: 'purchase' | 'sales' = 'sales';

    @Input() showDocumentStatuses: boolean;
    @Input() userType: ComplaintUserType;
    @Output() filtersEmitter = new EventEmitter<{
        filters: ComplaintFilters;
        reset: boolean;
    }>();

    constructor(
        private complaintService: ComplaintService,
        private router: Router,
        private route: ActivatedRoute,
        private config: ConfigService
    ) {

    }

    async ngOnInit() {
        await this.complaintService.workflowsLoaded;
        this.updateAvailableComplaintTypes();
    }

    ngOnChanges(): void {
        const hadNoContractors = !this.filteredContractorsList?.length;
        this.filteredContractorsList = this.contractorsList;
        this.filteredContractors = this.getContractorsList();

        // When contractors list loads for the first time, force ng-select to
        // re-evaluate currently selected value so it displays the name
        // instead of the raw company ID.
        if (hadNoContractors && this.filteredContractorsList?.length && this.userTypeCompanyId) {
            const currentValue = this.userTypeCompanyId;
            this.userTypeCompanyId = 0;
            setTimeout(() => {
                this.userTypeCompanyId = currentValue;
            });
        }
    }

    setFilters(filters: ComplaintFilters) {
        if (filters) {
            this._filters = filters;
            this.nrOrder = filters.nrOrder;
            this.status = filters.status;
            this.creationDateFrom = filters.creationDateFrom;
            this.creationDateTo = filters.creationDateTo;
            this.client = filters.client;
            this.distributorOrRetailer = filters.distributorOrRetailer;
            this.assignedUserId = filters.assignedUserId;
            this.userTypeCompanyId = filters.userTypeCompanyId;
            this.assignedTo = filters.assignedTo;
            this.companyId = filters.companyId;
            this.complaintType = filters.complaintType;
            this.accountManagerPersonId = filters.accountManagerPersonId;
        } else {
            this.resetFilters(false);
        }
    }

    handleStatus(value) {
        this.status = !isNaN(+value) ? +value : 0;
    }

    handleWorkers(value) {
        this.assignedUserId = !isNaN(+value) ? +value : 0;
    }

    handleAccountManager(value) {
        this.accountManagerPersonId = !isNaN(+value) ? +value : 0;
    }

    handleContractor(value: number) {
        const contractor = this.contractorsList?.find((el) => el.companyId === +value);
        this.assignedTo = contractor?.userType ?? null;
        this.userTypeCompanyId = !isNaN(+value) ? +value : 0;
    }

    handleCompanyId(value: number) {
        this.companyId = !isNaN(+value) ? +value : 0;
    }

    onListClick() {
        this.showMap.emit(false);
    }

    onMapClick() {
        this.showMap.emit(true);
    }

    onAssignedUserIdChange(event) {}

    onStatusChange(event) {
        this.status = +event;
    }

    onComplaintTypeChange(event) {
        this.complaintType = +event;
    }

    updateAvailableComplaintTypes(): void {
        this.availableComplaintTypes = this.getAvailableComplaintTypes();
    }

    getAvailableComplaintTypes(): Array<{ value: number; label: string }> {
        const workflows = this.complaintService.workflows || [];
        const availableTypes: Array<{ value: number; label: string }> = [];

        const hasComplaintType = workflows.some((w) => w.documentTypeId === 10 && w.active === true);
        const hasServiceType = workflows.some((w) => w.documentTypeId === 11 && w.active === true);
        const activeTypesCount = (hasComplaintType ? 1 : 0) + (hasServiceType ? 1 : 0);

        if (activeTypesCount >= 2) {
            availableTypes.push({ value: 0, label: 'COMPLAINT|Wszystkie' });
        }

        if (hasComplaintType) {
            availableTypes.push({ value: 10, label: 'COMPLAINT|Reklamacyjne' });
        }
        if (hasServiceType) {
            availableTypes.push({ value: 11, label: 'COMPLAINT|Płatne' });
        }

        return availableTypes;
    }

    emitFilters(reset = false) {
        this.filtersEmitter.emit({
            filters: {
                assignedUserId: this.assignedUserId,
                client: this.client,
                nrOrder: this.nrOrder,
                distributorOrRetailer: this.distributorOrRetailer,
                status: this.status,
                creationDateFrom: this.creationDateFrom,
                creationDateTo: this.creationDateTo,
                userTypeCompanyId: this.userTypeCompanyId,
                assignedTo: this.assignedTo,
                companyId: this.companyId,
                complaintType: this.complaintType,
                accountManagerPersonId: this.accountManagerPersonId,
            },
            reset,
        });
    }

    resetFilters(emit = true) {
        this.nrOrder = '';
        this.assignedUserId = 0;
        this.accountManagerPersonId = 0;
        this.status = 0;
        this.creationDateFrom = null;
        this.creationDateTo = null;
        this.client = '';
        this.distributorOrRetailer = '';
        this.userTypeCompanyId = this.config.user?.user?.ActiveCompany ? +this.config.user.user.ActiveCompany : 0;
        this.assignedTo = this.complaintService.getUserType();
        this.companyId = 0;
        this.complaintType = 0;
        if (emit) this.emitFilters(true);
    }

    getUserTypeName(type: ComplaintUserType) {
        return this.complaintService.getUserTypeName(type);
    }

    getContractorsList() {
        return this.contractorsList?.filter(
            (contractor) =>
                (this.userType === 'distributor' && contractor.userType === 'authorizedRetailer') ||
                (this.userType === 'manufacturer' &&
                    (contractor.userType === 'distributor' ||
                        contractor.userType === 'authorizedRetailer'))
        );
    }

    assignedToMe() {
        this.assignedUserId = this.config.user.user?.id ?? 0;
        this.userTypeCompanyId = this.config.user.user?.ActiveCompany ? +this.config.user.user?.ActiveCompany : 0;
        this.assignedTo = this.userType
        this.emitFilters(true);
    }
}
